import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import classNames from 'classnames';
import { NewBookingsStore, RootStore } from 'AppClient/stores';
import { isEmpty, without, isNil } from 'lodash-es';
import Alert from 'components/antd/Alert/Alert';
import { ThemeContext } from 'app-themes';
import styles from './styles.module.scss';
import { ProviderRowActive } from './ProviderRowActive';
import { ProviderRowInactive } from './ProviderRowInactive';
import TermsAndConditionsModal from '../TermsAndConditionsModal';

interface Props {
	newBookingsStore?: NewBookingsStore;
}

interface GetTitle {
	title: string;
	text?: string;
}

@inject(({ newBookingsStore }: RootStore) => ({ newBookingsStore }))
@observer
export class ChooseProvider extends Component<Props> {
	static contextType = ThemeContext;

	getTitle = ({ title, text }: GetTitle) => (
		<div className={styles.providerCategory}>
			<h3 className={styles.providerCategoryTitle}>{title}</h3>
			{text && <div className={styles.providerCategoryInfo}>{text}</div>}
		</div>
	);

	getInitiallySelected = () => {
		const { bookBookingData, upcomingBookingData } = this.props.newBookingsStore!;
		const { bookingPrices = [] } = bookBookingData;

		return bookingPrices.find(b => {
			if (upcomingBookingData) {
				return (
					(!isNil(b.servicePartnerId) &&
						b.servicePartnerId === upcomingBookingData.servicePartnerId) ||
					(!isNil(b.integratedServicePartnerId) &&
						b.integratedServicePartnerId ===
							upcomingBookingData.integratedServicePartnerId)
				);
			}
			return false;
		});
	};

	render() {
		const {
			bookBookingData,
			bookingPriceId,
			getFullTripEstimate,
			isCreditCardValid,
			upcomingBookingData,
			toggleTermsAndConditionsModal,
			setTermsAndConditionsAcceptModalType,
		} = this.props.newBookingsStore!;
		const { bookingPrices = [], customerCurrencySymbol } = bookBookingData;
		const theme = this.context;
		const isEditingMode = !!upcomingBookingData;
		const initiallySelected = this.getInitiallySelected();
		let data = [...bookingPrices];

		if (initiallySelected) {
			data = without(data, initiallySelected);
		}

		return (
			<div
				className={classNames(styles.provider, {
					[styles[`provider-${theme}`]]: theme !== '',
				})}
			>
				<div className={styles.providerTitle}>
					<h2>Choose partner</h2>
					<span>{getFullTripEstimate}</span>
				</div>
				{isEmpty(bookingPrices) && !isEditingMode ? (
					<Alert
						type="info"
						message={`All service partners are busy at this time, please amend your details.`}
						showIcon
						isStatic
					/>
				) : (
					<>
						<div className="modalBox modalBoxBooking">
							<div className="modalBoxRow">
								<div className="modalBoxCol">
									<div className={styles.providerList}>
										{initiallySelected || isEditingMode
											? this.getTitle({ title: 'Initially selected' })
											: null}

										{initiallySelected ? (
											<div className={styles.providerInitiallySelected}>
												<ProviderRowActive
													bookingPrice={initiallySelected}
													customerCurrencySymbol={customerCurrencySymbol}
													isSelected={
														bookingPriceId ===
														initiallySelected.bookingPriceId
													}
													disabledAction={!isCreditCardValid}
													onClickTermsAndConditions={
														toggleTermsAndConditionsModal
													}
													setTermsAndConditionsAcceptModalType={
														setTermsAndConditionsAcceptModalType
													}
												/>
											</div>
										) : isEditingMode && upcomingBookingData ? (
											<ProviderRowInactive
												bookingData={upcomingBookingData}
												disableTermsAndConditions
											/>
										) : null}

										{initiallySelected && !isEditingMode && data.length
											? this.getTitle({
													title: 'Other quotes',
											  })
											: null}
										{isEditingMode && data.length
											? this.getTitle({
													title: 'Other quotes',
													text:
														'To select another quote, please cancel current booking and create a new one',
											  })
											: null}

										{data.map(bookingPrice => {
											return (
												<ProviderRowActive
													key={bookingPrice.bookingPriceId}
													bookingPrice={bookingPrice}
													customerCurrencySymbol={customerCurrencySymbol}
													isSelected={
														bookingPriceId ===
														bookingPrice.bookingPriceId
													}
													disabledAction={!isCreditCardValid}
													hideAction={isEditingMode}
													onClickTermsAndConditions={
														toggleTermsAndConditionsModal
													}
													setTermsAndConditionsAcceptModalType={
														setTermsAndConditionsAcceptModalType
													}
												/>
											);
										})}
									</div>
								</div>
							</div>
						</div>
						<TermsAndConditionsModal />
					</>
				)}
			</div>
		);
	}
}
